import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <a name="module-xmp" id="module-xmp"></a>
    <h1 {...{
      "id": "requireuxpxmp"
    }}>{`require('uxp').xmp`}</h1>
    <p><strong parentName="p">{`Since`}</strong>{`: v7.2.0`}</p>
    <br></br>
    <p>{`Adobe's XMP (Extensible Metadata Platform) is a metadata standard developed by Adobe Systems. It allows users to embed descriptive, rights, and other metadata into digital files.
XMP is extensible and customizable, supporting various types of information and user-defined schemas. It is compatible with Adobe software and third-party applications, promoting interoperability.
Based on open standards, XMP follows XML syntax and RDF model. It enhances searchability, organization, and rights management of files. Overall, XMP facilitates efficient metadata management and effective digital asset management.
For more information about XMP metadata, see the `}<a parentName="p" {...{
        "href": "https://www.adobe.com/devnet/xmp.html"
      }}>{`XMP Specification at Adobe Developer Center`}</a>{`.`}</p>
    <br></br>
    <p>{`XMP module in UXP offers JavaScript access to the Adobe XMP Core and XMP Files libraries.
To use the XMP objects, you must import the XMP module.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const xmp = require("uxp").xmp;
`}</code></pre>
    <p>{`After importing the module, you can access the top-level objects of the XMP scripting API.
| Class | Description |
| --- | --- |
| `}<a parentName="p" {...{
        "href": "../XMP%20Classes/XMPMeta.md"
      }}>{`XMPMeta`}</a>{` | Provides interface to work with XMP metadata (got via the hosts DOM APIs) for reading, writing and modifying. |
| `}<a parentName="p" {...{
        "href": "../XMP%20Classes/XMPFile.md"
      }}>{`XMPFile`}</a>{` | Provides interface to work with XMP metadata on a file directly, (instead of depending on host to fetch the XMP metadata contents). |
| `}<a parentName="p" {...{
        "href": "../XMP%20Classes/XMPUtils.md"
      }}>{`XMPUtils`}</a>{` | Provides additional utility functions for array handling. |
| `}<a parentName="p" {...{
        "href": "../XMP%20Classes/XMPDateTime.md"
      }}>{`XMPDateTime`}</a>{` | Represents date-time values. |
| `}<a parentName="p" {...{
        "href": "../XMP%20Classes/XMPConst.md"
      }}>{`XMPConst`}</a>{` | Contains numeric and string constant values for use with the JavaScript API. |`}</p>
    <p>{`The top-level objects provide access to additional support classes that encapsulate individual metadata properties, file information, and XMP packet information, and a utility that allows iteration through properties.
| Class | Description |
| --- | --- |
| `}<a parentName="p" {...{
        "href": "../XMP%20Classes/XMPIterator.md"
      }}>{`XMPIterator`}</a>{` | Allows iteration through properties in an XMPMeta object. |
| `}<a parentName="p" {...{
        "href": "../XMP%20Classes/XMPProperty.md"
      }}>{`XMPProperty`}</a>{` | Describes a metadata property. |
| `}<a parentName="p" {...{
        "href": "../XMP%20Classes/XMPFileInfo.md"
      }}>{`XMPFileInfo`}</a>{` | Describes a file. |
| `}<a parentName="p" {...{
        "href": "../XMP%20Classes/XMPPacketInfo.md"
      }}>{`XMPPacketInfo`}</a>{` | Describes a raw XMP packet in a file. |`}</p>
    <p>{`See specific object reference for details of the class, its properties and methods.`}</p>
    <h1 {...{
      "id": "using-the-xmp-scripting-api"
    }}>{`Using the XMP scripting API`}</h1>
    <p>{`The XMPMeta object is the primary means of access to the namespaces and properties of an XMP metadata packet. Through this object, you can create and delete namespaces and properties, and examine and modify property values.
You can obtain or create an XMPMeta object in several ways:`}</p>
    <p>{`You can use an XMPFile object to retrieve existing metadata directly from a file. The XMPFile.getXMP() method creates an XMPMeta object, which you can use to examine or modify the properties and their values. You can then use XMPFile.putXMP() to write the modified metadata back to the file.`}</p>
    <p>{`You can create an XMPMeta object with the constructor, initializing it with an XMP packet created or obtained elsewhere.`}</p>
    <p>{`You can create a new, empty XMPMeta object with the constructor, and use its methods to create entirely new namespaces and properties. You can then use XMPFile.putXMP() to inject the new metadata into a file.`}</p>
    <h1 {...{
      "id": "creating-new-metadata"
    }}>{`Creating new metadata`}</h1>
    <p>{`This code creates an empty XMPMeta object, uses it to set a metadata property, and serializes it to a string, which you could pass to an authoring tool, for example, or store in a file.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const {XMPMeta, XMPConst} = require("uxp").xmp;
let xmp = new XMPMeta();
xmp.setProperty( XMPConst.NS_XMP, "CreatorTool", "My Script" );
let xmpStr = xmp.serialize(); // Serialize the XMP packet to XML

// Retrieve property
let prop = xmp.getProperty(XMPConst.NS_XMP, "CreatorTool");
console.log( \`namespace: \${prop.namespace}, property path + name: \${prop.path}, value: \${prop.value}\`);
`}</code></pre>
    <h1 {...{
      "id": "modifying-existing-metadata"
    }}>{`Modifying existing metadata`}</h1>
    <p>{`This code accesses an existing XMP packet, assuming the location has been assigned to a string letiable. It sets the modification-date property to the current date and time, and stores the updated XMP packet back to the string, making it as small as possible.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const { XMPMeta, XMPConst, XMPDateTime } = require("uxp").xmp;
let xmp = new XMPMeta(xmpStr); // Object initialized with xmp packet as string
let dateTime = new XMPDateTime( new Date() ); // Now
let oldModificationDate = mp.getProperty( XMPConst.NS_XMP, "ModifyDate", "xmpdate" );
console.log( "Old modification date: " + oldModificationDate );
xmp.setProperty( XMPConst.NS_XMP, "ModifyDate", dateTime, "xmpdate" );

// Serialize to XML, in compact style
let xmpStr = xmp.serialize( XMPConst.SERIALIZE_USE_COMPACT_FORMAT );
`}</code></pre>
    <h1 {...{
      "id": "using-xmpfile-for-batch-processing"
    }}>{`Using XMPFile for batch processing`}</h1>
    <p>{`This example iterates through a folder of image files and processes the metadata. The script processes each picture as follows:`}</p>
    <p>{`Reads and parses the metadata. If an image file does not contain XMP metadata, the legacy metadata is automatically converted to XMP.`}</p>
    <p>{`Deletes the list of existing creators, and adds a new creator value.`}</p>
    <p>{`Writes the modified metadata back to the file.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`console.log( "XMPFiles batch processing example" );
// Load the XMPScript library
const {XMPMeta, XMPConst, XMPFile} = require("uxp").xmp;

// Iterate through the photos in the folder
const uxpfs = require("uxp").storage;
const ufs = uxpfs.localFileSystem;
let folder = await ufs.getFolder({initialDomain: uxpfs.domains.userDocuments});
let files = await folder.getEntries();
files.forEach((file) => {
     console.log( "Process file: " + file.name );

     // Applies only to files, not to folders
     if ( file instanceof Entry ) {
         let xmpFile = new XMPFile( file.nativePath, XMPConst.UNKNOWN, XMPConst.OPEN_FOR_UPDATE );
         let xmp = xmpFile.getXMP();

         // Delete existing authors and add a new one
         // Existing metadata stays untouched
         xmp.deleteProperty( XMPConst.NS_DC, "creator" );
         xmp.appendArrayItem( XMPConst.NS_DC, "creator", "Judy", 0, XMPConst.ARRAY_IS_ORDERED );

         // Write updated metadata into the file
         if ( xmpFile.canPutXMP( xmp ) ) {
             xmpFile.putXMP( xmp );
         }
         xmpFile.closeFile( XMPConst.CLOSE_UPDATE_SAFELY );
     }
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      